import { useOnboarding, PRICING } from "./OnboardingContext";
import { FileSignature, CheckCircle, ArrowLeft, ArrowRight, Loader2, Calendar, AlertCircle, ExternalLink, Clock, Info } from "lucide-react";
import { useState, useEffect } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StepSignature = () => {
  const { 
    signatureStatus, 
    setSignatureStatus, 
    nextStep, 
    prevStep, 
    getPackageDetails,
    billingInfo,
    monthlyPrice,
    setupFee,
    selectedPackage,
    selectedAddOns
  } = useOnboarding();
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [envelopeId, setEnvelopeId] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  
  const pkg = getPackageDetails();
  const hasAcceleratedDeployment = selectedAddOns.includes("acceleratedDeployment");

  // Check for DocuSign envelope ID in localStorage (for display purposes)
  useEffect(() => {
    const savedEnvelopeId = localStorage.getItem('docusign_envelope_id');
    if (savedEnvelopeId && signatureStatus === "signed") {
      setEnvelopeId(savedEnvelopeId);
    }
  }, [signatureStatus]);

  const handleSignature = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const currentUrl = window.location.origin + window.location.pathname;
      const returnUrl = `${currentUrl}?event=signing_complete`;
      
      const response = await fetch(`${API_URL}/api/docusign/create-envelope`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          signer_email: billingInfo.email || "client@example.com",
          signer_name: billingInfo.contactName || billingInfo.companyName || "Client",
          company_name: billingInfo.companyName || "Entreprise Cliente",
          package_name: pkg?.name || "Package",
          monthly_price: monthlyPrice || 0,
          setup_fee: setupFee || 0,
          engagement_months: pkg?.engagement || 3,
          return_url: returnUrl,
          is_preorder: true,
          accelerated_deployment: hasAcceleratedDeployment
        })
      });
      
      const data = await response.json();
      
      if (data.success && data.signing_url) {
        localStorage.setItem('docusign_envelope_id', data.envelope_id);
        setEnvelopeId(data.envelope_id);
        window.location.href = data.signing_url;
      } else {
        setError(data.error || "Erreur lors de la création du contrat. Veuillez réessayer.");
        setIsLoading(false);
      }
      
    } catch (err) {
      setError("Erreur de connexion. Veuillez vérifier votre connexion internet.");
      setIsLoading(false);
    }
  };

  const checkEnvelopeStatus = async () => {
    if (!envelopeId) return;
    
    setCheckingStatus(true);
    try {
      const response = await fetch(`${API_URL}/api/docusign/envelope/${envelopeId}/status`);
      const data = await response.json();
      
      if (data.status === 'completed') {
        setSignatureStatus("signed");
      }
    } catch (err) {
      // Error handled silently
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleSimulatedSignature = async () => {
    setIsLoading(true);
    setError(null);
    await new Promise((resolve) => setTimeout(resolve, 2000));
    setSignatureStatus("signed");
    setIsLoading(false);
  };

  if (!pkg) return null;

  return (
    <div data-testid="step-signature" className="animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <p className="font-body text-sm tracking-[0.15em] uppercase text-[#B89F7C] mb-2">
          Étape 6
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-[#0A0A0A] mb-4">
          Signature du contrat de précommande
        </h1>
        <p className="font-body text-[#525252]">
          Signez électroniquement votre contrat de réservation via DocuSign.
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-6 flex items-start gap-3">
          <AlertCircle size={20} className="shrink-0 mt-0.5" />
          <div>
            <p className="font-body text-sm">{error}</p>
            <button 
              onClick={() => setError(null)}
              className="text-sm underline mt-1 hover:no-underline"
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      {/* Pre-order Summary Banner */}
      <div className="bg-[#0A0A0A] text-white p-6 mb-8">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-[#B89F7C]/20 flex items-center justify-center shrink-0">
            <Clock size={20} className="text-[#B89F7C]" />
          </div>
          <div>
            <h3 className="font-heading text-lg mb-3">Avant de signer, veuillez noter</h3>
            <ul className="space-y-2">
              <li className="font-body text-sm text-white/80 flex items-start gap-2">
                <span className="text-[#B89F7C] mt-1">•</span>
                Ce contrat est un contrat de <span className="text-[#B89F7C] font-medium">précommande</span>. 
                La mise en service n'est pas immédiate.
              </li>
              <li className="font-body text-sm text-white/80 flex items-start gap-2">
                <span className="text-[#B89F7C] mt-1">•</span>
                Le projet entre en phase de préparation et de planification après signature.
              </li>
              <li className="font-body text-sm text-white/80 flex items-start gap-2">
                <span className="text-[#B89F7C] mt-1">•</span>
                <span>
                  Première mise en service estimée : <span className="text-[#B89F7C] font-medium">entre 1 et 4 mois</span>, 
                  selon les disponibilités opérationnelles et les contraintes du réseau partenaire.
                </span>
              </li>
              {hasAcceleratedDeployment && (
                <li className="font-body text-sm text-white/80 flex items-start gap-2">
                  <CheckCircle size={14} className="text-[#B89F7C] mt-1 shrink-0" />
                  <span>
                    <span className="text-[#B89F7C] font-medium">Déploiement accéléré activé</span> — 
                    Votre projet bénéficie d'une priorité dans le planning de mise en service.
                  </span>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Contract Terms */}
      <div className="bg-[#F9F6F0] border border-[#E5E5E5] p-6 mb-8">
        <h3 className="font-heading text-lg text-[#0A0A0A] mb-4 flex items-center gap-2">
          <FileSignature size={20} className="text-[#B89F7C]" />
          Conditions contractuelles
        </h3>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Calendar size={18} className="text-[#B89F7C] mt-1 shrink-0" />
            <div>
              <p className="font-body text-sm font-medium text-[#0A0A0A]">
                Engagement minimum : {pkg.engagement} mois
              </p>
              <p className="font-body text-xs text-[#525252]">
                À compter de la mise en service effective du dispositif. Renouvellement tacite sauf résiliation.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Clock size={18} className="text-[#B89F7C] mt-1 shrink-0" />
            <div>
              <p className="font-body text-sm font-medium text-[#0A0A0A]">
                Délai de mise en service
              </p>
              <p className="font-body text-xs text-[#525252]">
                Estimé entre 1 et 4 mois selon disponibilités opérationnelles et contraintes du réseau partenaire.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Info size={18} className="text-[#B89F7C] mt-1 shrink-0" />
            <div>
              <p className="font-body text-sm font-medium text-[#0A0A0A]">
                Facturation
              </p>
              <p className="font-body text-xs text-[#525252]">
                Frais de réservation à la signature. Facturation mensuelle à compter de la mise en service effective.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="text-[#B89F7C] mt-1 shrink-0" />
            <div>
              <p className="font-body text-sm font-medium text-[#0A0A0A]">
                Conditions de résiliation
              </p>
              <p className="font-body text-xs text-[#525252]">
                Préavis de 30 jours avant la fin de la période d'engagement.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Signature Card */}
      <div className="bg-white border border-[#E5E5E5] p-8 mb-8">
        {signatureStatus === "pending" ? (
          <div className="text-center py-8">
            <div className="w-20 h-20 mx-auto mb-6 bg-[#F9F6F0] flex items-center justify-center">
              <FileSignature size={36} className="text-[#B89F7C]" strokeWidth={1.5} />
            </div>
            <h3 className="font-heading text-2xl text-[#0A0A0A] mb-4">
              Contrat de précommande
            </h3>
            <p className="font-body text-[#525252] mb-8 max-w-md mx-auto">
              Nous utilisons DocuSign pour une signature électronique sécurisée et légalement reconnue.
            </p>

            <button
              onClick={handleSignature}
              disabled={isLoading}
              data-testid="btn-docusign"
              className="bg-[#0A0A0A] text-white hover:bg-[#B89F7C] transition-colors duration-300 px-10 py-4 text-sm tracking-widest uppercase font-medium inline-flex items-center gap-3 disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Préparation de DocuSign...
                </>
              ) : (
                <>
                  <FileSignature size={18} />
                  Signer le contrat de précommande
                  <ExternalLink size={14} />
                </>
              )}
            </button>

            {error && (
              <div className="mt-6 pt-6 border-t border-[#E5E5E5]">
                <p className="font-body text-xs text-[#525252] mb-3">
                  En cas de problème avec DocuSign, vous pouvez simuler la signature :
                </p>
                <button
                  onClick={handleSimulatedSignature}
                  disabled={isLoading}
                  className="text-[#B89F7C] hover:text-[#0A0A0A] transition-colors text-sm underline"
                >
                  Simuler la signature (démo)
                </button>
              </div>
            )}

            <p className="font-body text-xs text-[#525252] mt-6">
              Vous serez redirigé vers DocuSign pour signer le contrat.
              <br />
              La signature prend moins de 2 minutes.
            </p>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-20 h-20 mx-auto mb-6 bg-green-50 flex items-center justify-center">
              <CheckCircle size={40} className="text-green-600" strokeWidth={1.5} />
            </div>
            <h3 className="font-heading text-2xl text-[#0A0A0A] mb-2">
              Contrat signé
            </h3>
            <p className="font-body text-[#525252] mb-4">
              Votre contrat de précommande a été signé avec succès.
            </p>
            {envelopeId && (
              <p className="font-body text-xs text-[#525252] mb-4">
                Référence : {envelopeId.substring(0, 8)}...
              </p>
            )}
            <p className="font-body text-sm text-[#B89F7C]">
              Une copie vous sera envoyée par email.
            </p>
          </div>
        )}
      </div>

      {/* Contract Preview */}
      <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-6 mb-8">
        <h4 className="font-body text-sm tracking-[0.15em] uppercase text-[#525252] mb-4">
          Aperçu du contrat
        </h4>
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-[#525252]">Type de contrat</span>
            <span className="text-[#0A0A0A] font-medium">Précommande</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[#525252]">Package</span>
            <span className="text-[#0A0A0A] font-medium">{pkg.name}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[#525252]">Redevance mensuelle</span>
            <span className="text-[#0A0A0A] font-medium">{monthlyPrice}€ HT</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[#525252]">Frais de réservation</span>
            <span className="text-[#0A0A0A] font-medium">{setupFee}€ HT</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[#525252]">Engagement</span>
            <span className="text-[#0A0A0A] font-medium">{pkg.engagement} mois</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[#525252]">Mise en service estimée</span>
            <span className="text-[#0A0A0A] font-medium">1 à 4 mois</span>
          </div>
        </div>
        <p className="font-body text-xs text-[#525252] mt-4 pt-4 border-t border-[#E5E5E5]">
          Le contrat complet incluant les conditions de précommande sera présenté lors de la signature via DocuSign.
        </p>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t border-[#E5E5E5]">
        <button
          onClick={prevStep}
          data-testid="btn-prev-step"
          className="flex items-center gap-2 font-body text-sm text-[#525252] hover:text-[#0A0A0A] transition-colors"
        >
          <ArrowLeft size={16} />
          Retour
        </button>
        <button
          onClick={nextStep}
          disabled={signatureStatus !== "signed"}
          data-testid="btn-next-step"
          className="bg-[#0A0A0A] text-white hover:bg-[#B89F7C] transition-colors duration-300 px-8 py-3 text-sm tracking-widest uppercase font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Valider la précommande
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default StepSignature;
